import { useState } from 'react';
import { validateForm } from '../utils/validation';
import { getPreferences, savePreferences, deletePreferences } from '../services/api';
import useCognitoAuth from '../authorization/cognitoAuthorizer';

export const useFormState = (initialPreferences?: any, onNotification?: (type: string, message: string, header?: string) => void) => {
  const { authToken } = useCognitoAuth();
  const [formState, setFormState] = useState(
    initialPreferences || {
      teamName: '',
      reminderMethod: 'sms',
      phoneNumber: '',
      telegramChatId: '',
      remindersEnabled: true,
      remindersTime: 3
    }
  );

  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [hasPreferences, setHasPreferences] = useState(!!initialPreferences);
  const [showTelegramBanner, setShowTelegramBanner] = useState(false);

  const handleInputChange = (field: string, value: any) => {
    setFormState(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = validateForm(formState);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validate()) {
      const success = await savePreferences(formState, authToken);
      if (success) {
        setHasPreferences(true);
        setIsEditing(false);
        setShowTelegramBanner(formState.reminderMethod === 'telegram');
        onNotification?.('success', 'Your reminder preferences have been saved successfully.');
      } else {
        onNotification?.('error', 'Unable to save your preferences. Please try again.', 'Save failed');
      }
    }
  };

  const handleCancel = async () => {
    if (hasPreferences) {
      const prefs = await getPreferences(authToken);
      if (prefs) {
        setFormState(prefs);
      }
    }
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleOptIn = () => {
    setIsEditing(true);
  };

  const handleOptOut = async () => {
    const success = await deletePreferences(authToken);
    if (success) {
      setFormState({
        teamName: '',
        reminderMethod: 'sms',
        phoneNumber: '',
        telegramChatId: '',
        remindersEnabled: true,
        remindersTime: 3
      });
      setHasPreferences(false);
      setIsEditing(false);
      onNotification?.('success', 'Your data has been removed successfully.');
    } else {
      onNotification?.('error', 'Unable to opt out. Please try again.', 'Opt out failed');
    }
  };

  return {
    formState,
    errors,
    isEditing,
    hasPreferences,
    showTelegramBanner,
    handleInputChange,
    handleSubmit,
    handleCancel,
    handleEdit,
    handleOptIn,
    handleOptOut,
    dismissTelegramBanner: () => setShowTelegramBanner(false)
  };
};
